/**
 * ClinicalReviewByline — "Clinically Reviewed by" module rendered on the
 * /locations directory and each clinic detail page.
 *
 * Reads reviewers from `data/clinics.js` (source of truth) so a reviewer
 * change propagates site-wide from one edit. Names link to the existing
 * provider profiles. Dr. Quirola's DNP status is shown inline so "Dr."
 * cannot be misread as MD/DO.
 *
 * Intentionally omits a review date — the spec forbids inventing one, and
 * the audit ledger lives on each provider's audited record, not here.
 */
import { Link } from "react-router-dom";
import { ShieldCheck } from "lucide-react";
import { CLINIC_REVIEWERS } from "../data/clinics";

export default function ClinicalReviewByline({ compact = false, testId = "clinical-review-byline" }) {
  return (
    <div
      data-testid={testId}
      className={`rounded-xl border border-stone-200 bg-stone-50 ${compact ? "p-4" : "p-5 md:p-6"}`}
    >
      <div className="flex items-start gap-3">
        <div className="mt-0.5 w-8 h-8 rounded-full bg-[color:var(--vv-teal)]/10 text-[color:var(--vv-teal)] grid place-items-center flex-shrink-0">
          <ShieldCheck size={16} />
        </div>
        <div className="flex-1">
          <div className="text-[11px] uppercase tracking-widest text-stone-500 font-semibold">Clinically Reviewed by</div>
          <ul className={`mt-2 space-y-1.5 ${compact ? "text-sm" : "text-sm md:text-[15px]"}`} data-testid={`${testId}-list`}>
            {CLINIC_REVIEWERS.map((r) => (
              <li key={r.profile} className="text-stone-800 leading-snug" data-testid={`${testId}-${r.profile.split("/").pop()}`}>
                <Link to={r.profile} className="font-semibold text-[color:var(--vv-ink)] hover:text-[color:var(--vv-teal)] underline decoration-stone-300 hover:decoration-[color:var(--vv-teal)]">
                  {r.name}
                </Link>
                <span className="text-stone-600">, {r.credentials}</span>
                {r.dnp_disclosure ? (
                  <div className="text-xs text-stone-500 mt-0.5">{r.dnp_disclosure}</div>
                ) : null}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
